import React, {
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { DescriptiveTags } from '@r6digital/rapidstor-types/types/Descriptor';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';

import store from '@/store';
import { setDetailsModal } from '@/store/bookingSlice';

import QuickSizeFilter from '../components/elements/quick-size-filter';
import SpaceCalculatorModal from '../components/space-calculator';
import SpaceCalculatorButton from '../components/space-calculator/SpaceCalculatorButton';
import { isParkingTypeName } from '../components/space-calculator/calculator';
import useSpaceCalculator from '../components/space-calculator/useSpaceCalculator';
import UnitTypeCard from '../components/unit-type';
import { defaultQuickSizeOptions, filterUnitTypesBySize } from '../filters';
import useListingSizeFilter from '../useListingSizeFilter';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { getFloorString, isUnitTypeAvailable } from '@/features/rapidstor/helpers';

import classes from '../scss/index.module.scss';
import SkeletonLoader from '@/components/SkeletonLoader';
import { descriptorDimensions } from '@/core/utils/rapidstor';
import { applyUnitFilters, isUnitTypePermitted } from '@/core/utils/unitFilters';

export interface UnitTypeWithImages extends UnitType {
    picture: string;
    slides: string[];
    tags: DescriptiveTags[];
    description: string;
    descriptor: Descriptor;
}

/**
 * Get the comparison value for a unit type based on a sort criterion.
 */
const getCompareValue = (ut: UnitType, criterion: string): number => {
    switch (criterion) {
        case 'size':
            return ut.area;
        case 'price':
            // Use discounted rate if available, otherwise regular rate
            return ut.rates.discounted
                ? ut.rates.discountedRate
                : ut.rates.rate;
        case 'availability':
            // More available = should come first, so negate for ascending sort
            return -ut.units.length;
        case 'floor':
            return ut.floor;
        default:
            return 0;
    }
};

/**
 * Sort unit types according to location.settings.unitSortOrder.
 * The first item in unitSortOrder is the PRIMARY sort criterion.
 * E.g., ['size', 'price', 'availability'] means: sort by size first,
 * then by price for equal sizes, then by availability for equal size+price.
 */
const sortUnitTypesForListing = <T extends UnitType>(
    unitTypes: T[],
    unitSortOrder: string[],
): T[] => [...unitTypes].sort((a, b) => {
        // Apply sort criteria in order: first criterion is primary
        for (const criterion of unitSortOrder) {
            const aVal = getCompareValue(a, criterion);
            const bVal = getCompareValue(b, criterion);
            if (aVal !== bVal) {
                return aVal - bVal;
            }
        }
        return 0;
    });

type FloorGroup = 'ground' | 'upper' | 'driveup';

/**
 * Adapted from Rapidstor's standardUnitListingMultilevelService.determineFloor.
 * Floors below the configured ground floor are excluded (null).
 */
const determineFloor = (
    ut: UnitType,
    groundFloor: number,
): FloorGroup | null => {
    if (ut.floor === groundFloor && !ut.inside) {
        return 'driveup';
    }
    if (ut.floor === groundFloor && ut.inside) {
        return 'ground';
    }
    if (ut.floor > groundFloor) {
        return 'upper';
    }
    return null;
};

/**
 * Adapted from Rapidstor's distributeUnitsToGroups/distributeUnitTypesToDescriptors.
 * Keeps at most one unit type per floor: an available one wins over an
 * unavailable one, and the cheaper rate wins between two of the same
 * availability.
 */
const pickUnitTypePerFloor = <T extends UnitType>(
    group: T[],
    groundFloor: number,
): T[] => {
    const floors: Record<FloorGroup, T | null> = {
        ground: null,
        upper: null,
        driveup: null,
    };
    for (const ut of group) {
        const floor = determineFloor(ut, groundFloor);
        if (!floor) {
            continue;
        }
        const existing = floors[floor];
        if (!existing) {
            floors[floor] = ut;
            continue;
        }
        const newAvailable = isUnitTypeAvailable(ut);
        const existingAvailable = isUnitTypeAvailable(existing);
        // discountedRate is always set (equals rate when not discounted)
        const newRate = ut.rates.discountedRate;
        const existingRate = existing.rates.discountedRate;
        if (
            (newAvailable && !existingAvailable) ||
            (newAvailable && newRate < existingRate) ||
            (!newAvailable && !existingAvailable && newRate < existingRate)
        ) {
            floors[floor] = ut;
        }
    }
    return [floors.ground, floors.upper, floors.driveup].filter(
        Boolean,
    ) as T[];
};

function StandardUnitListingIndex() {
    const {
        descriptorList,
        location,
        unitListLoading,
        unitListSuccess,
    } =
        useContext(JourneyContext);
    const { trackEvent } = useRapidStorTracking();
    const { t } = useTranslation();

    // Read per render: the dev config modal replaces rapidstor_options in place.
    const { unitFilters, quickSizeFilters } = window.rapidstor_settings.rapidstor_options;

    // Before grouping, so a hidden unit type cannot take a floor or descriptor slot.
    const permittedDescriptors = useMemo(
        () => applyUnitFilters(descriptorList, unitFilters),
        [descriptorList, unitFilters],
    );

    const quickSizeOptions = useMemo(() => {
        if (quickSizeFilters?.length) return quickSizeFilters;
        return location ? defaultQuickSizeOptions(location) : [];
    }, [quickSizeFilters, location]);

    const {
        activeQuickSizeKey,
        rules: sizeRules,
        selectQuickSize,
        applySpaceCalculatorResult,
    } = useListingSizeFilter(quickSizeOptions);

    const { unitTypes, groupedUnitTypes, isGrouped } = useMemo(() => {
        const groupUnitTypesByFloor =
            location?.rapidstor.useMultilevel ?? false;
        const groupDescriptors = location?.rapidstor.groupDescriptors ?? false;

        const all: UnitTypeWithImages[] = permittedDescriptors
            .map((d) => d.unitTypes.map((ut) => ({
                ...ut,
                picture: d.picture,
                slides: d.slides,
                tags: d.tags || [],
                description: d.description || '',
                descriptor: d,
            }))).flat() as UnitTypeWithImages[];

        const sortOrder = location?.settings.unitSortOrder || ['size', 'price', 'availability'];
        const groundFloor = location?.settings.groundFloor ?? 0;

        if (!groupUnitTypesByFloor) {
            // If we are not grouping by floor but need to group descriptors, keep only
            // a single representative per descriptor: the first available unit type
            // after sorting, falling back to the first sorted one (legacy
            // getUnitTypeToUse behaviour)
            if (groupDescriptors) {
                const map: Record<string, UnitTypeWithImages[]> = {};
                for (const ut of all) {
                    (map[ut.descriptor?._id ?? 'ungrouped'] ||= []).push(ut);
                }
                const deduped: UnitTypeWithImages[] = Object.values(map).map(
                    (group) => {
                        const sorted = sortUnitTypesForListing(group, sortOrder);
                        return sorted.find(isUnitTypeAvailable) ?? sorted[0];
                    },
                );
                // Sort the deduped array so the listing is in correct order
                return {
                    unitTypes: sortUnitTypesForListing(deduped, sortOrder),
                    groupedUnitTypes: [] as UnitTypeWithImages[][],
                    isGrouped: false,
                };
            }
            // Standard flat listing - sort all unit types
            return {
                unitTypes: sortUnitTypesForListing(all, sortOrder),
                groupedUnitTypes: [] as UnitTypeWithImages[][],
                isGrouped: false,
            };
        }

        // Multilevel: group by descriptor (legacy buildGroupIdentifier), then keep
        // at most one unit type per floor within each group. Groups with no unit
        // type on any floor are dropped. The size filter applies first, as in the
        // legacy distributeUnitTypesToDescriptors, so a unit type outside it cannot
        // take a floor from one inside it.
        const map: Record<string, UnitTypeWithImages[]> = {};
        for (const ut of all.filter((unitType) => isUnitTypePermitted(unitType, unitType.descriptor?._id, sizeRules))) {
            (map[ut.descriptor?._id ?? 'ungrouped'] ||= []).push(ut);
        }
        // Sort groups by the first item's sort criteria, then sort within each group
        const sortedGroups = Object.values(map)
            .map((g) => pickUnitTypePerFloor(g, groundFloor))
            .filter((g) => g.length > 0)
            .map((g) => sortUnitTypesForListing(g, sortOrder))
            .map((g) => {
                // Collapse variants that share a floor category (Ground / Upper /
                // Drive Up) so each category shows a single option instead of one
                // per raw floor level. Keeps the best variant per category (first
                // after sorting).
                const seen = new Set<string>();
                return g.filter((ut) => {
                    const category = String(getFloorString(ut, location!));
                    if (seen.has(category)) return false;
                    seen.add(category);
                    return true;
                });
            })
            .sort((a, b) => {
                // Sort groups by comparing their first (best) unit type
                for (const criterion of sortOrder) {
                    const aVal = getCompareValue(a[0], criterion);
                    const bVal = getCompareValue(b[0], criterion);
                    if (aVal !== bVal) return aVal - bVal;
                }
                return 0;
            });
        return { unitTypes: all, groupedUnitTypes: sortedGroups, isGrouped: true };
    }, [permittedDescriptors, location, sizeRules]);

    const spaceCalculator = useSpaceCalculator(applySpaceCalculatorResult);

    const hasParkingUnitTypes = useMemo(
        () => permittedDescriptors.some((descriptor) => descriptor.unitTypes.some(
            (unitType) => isParkingTypeName(unitType.typeName),
        )),
        [permittedDescriptors],
    );

    const { filteredUnitTypes, filteredGroupedUnitTypes } = useMemo(() => {
        if (!location) {
            return {
                filteredUnitTypes: unitTypes,
                filteredGroupedUnitTypes: groupedUnitTypes,
            };
        }
        return filterUnitTypesBySize(
            unitTypes,
            groupedUnitTypes,
            sizeRules,
            location,
        );
    }, [unitTypes, groupedUnitTypes, sizeRules, location]);

    const onBookNow = (unitType: UnitTypeWithImages) => {
        trackEvent('book_online_clicked', {
            unitType: unitType.typeName,
            unitSize: descriptorDimensions(unitType, location!, { spaceAroundSeparator: false }),
        });
        store.dispatch(setDetailsModal({ isOpen: true, unitType, type: 'bookNow' }));
    };

    const onShowPrices = (unitType: UnitTypeWithImages) => {
        trackEvent('show_prices_clicked', {
            unitType: unitType.typeName,
            unitSize: descriptorDimensions(unitType, location!, { spaceAroundSeparator: false }),
        });
        store.dispatch(setDetailsModal({ isOpen: true, unitType, type: 'showPrices' }));
    };

    const onSendEnquiry = (unitType: UnitTypeWithImages) => {
        trackEvent('send_enquiry_clicked', {
            unitType: unitType.typeName,
            unitSize: descriptorDimensions(unitType, location!, { spaceAroundSeparator: false }),
        });
        store.dispatch(setDetailsModal({ isOpen: true, unitType, type: 'contactMe' }));
    };

    useEffect(() => {
        if (unitListSuccess && !unitListLoading) {
            trackEvent('unit-listing', {});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitListLoading, unitListSuccess]);

    if (!location) {
        return null;
    }

    const isListingEmpty = isGrouped
        ? filteredGroupedUnitTypes.length === 0
        : filteredUnitTypes.length === 0;

    return (
        <div className={classes.container} id="standard-unit-listing">
            <div className={classes.filterBar}>
                {!location.rapidstor.hideQuickSearch ? (
                    <QuickSizeFilter
                        options={quickSizeOptions}
                        value={activeQuickSizeKey}
                        onChange={selectQuickSize}
                    />
                ) : null}
                <div className={classes.spaceCalculatorTrigger}>
                    <SpaceCalculatorButton onClick={spaceCalculator.open} />
                </div>
            </div>

            {spaceCalculator.isOpen && (
                <SpaceCalculatorModal
                    location={location}
                    isVehicleStorageAvailable={hasParkingUnitTypes}
                    onClose={spaceCalculator.close}
                    onFinish={spaceCalculator.finish}
                />
            )}

            <div className={classes.unitTypeGrid} data-testid={`${unitListLoading ? 'unit-type-grid-loading' : 'unit-type-grid-loaded'}`}>
                {unitListLoading && <SkeletonLoader />}
                {!unitListLoading && isGrouped && filteredGroupedUnitTypes.length > 0 && filteredGroupedUnitTypes.map((group) => (
                    <UnitTypeCard
                        key={group.map((g) => g._id).join('-')}
                        unitTypes={group}
                        location={location}
                        onBookOnline={onBookNow}
                        onSendEnquiry={onSendEnquiry}
                        onShowPrices={onShowPrices}
                    />
                ))}
                {!unitListLoading && !isGrouped && filteredUnitTypes.map((ut) => (
                    <UnitTypeCard
                        key={ut._id}
                        unitType={ut}
                        location={location}
                        onBookOnline={onBookNow}
                        onSendEnquiry={onSendEnquiry}
                        onShowPrices={onShowPrices}
                    />
                ))}
                {!unitListLoading && unitListSuccess && isListingEmpty && (
                    <p className={classes.emptyState} data-testid="unit-listing-empty">
                        {t('standardUnitListing:noUnitsAvailable')}
                    </p>
                )}
            </div>
        </div>
    );
}

export default StandardUnitListingIndex;
